import { Injectable } from "@angular/core"
import { BehaviorSubject, type Observable, throwError, timer, type Subscription, of } from "rxjs"
import { tap, catchError, map, delay, retryWhen, take } from "rxjs/operators"
import { Router } from "@angular/router"
import { ApiService } from '../services/api.service';

// Interfaces para tipado de la respuesta de la API
interface LoginResponse {
  token: string
  abilities: string[]
  user: {
    id: string
    email: string
    name: string | null
    accountId: string
    roleId: string
    isFirstLogin: boolean
    isActive: boolean
    profileImageUrl: string
    role: {
      id: string
      name: string
      slug: string
      description: string
    }
  }
  account: {
    id: string
    name: string
    slug: string
    email: string
    logoUrl: string
  }
  client: {
    id: string
    name: string
    surname: string
    nif: string
    wristbands: any[]
  }
  clientId: string
}

interface LoginRequest {
  email: string
  password: string
}

interface TokenPayload {
  iss: string
  iat: number
  exp: number
  nbf: number
  jti: string
  sub: string
  prv: string
  id: string
  email: string
}

@Injectable({
  providedIn: "root"
})
export class AuthService {
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(this.hasToken())
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable()

  // Configuración de expiración
  private readonly INACTIVITY_TIMEOUT = 7 * 24 * 60 * 60 * 1000 // 7 días TOTALES sin usar la app
  private readonly REFRESH_BEFORE_EXPIRY = 10 * 60 * 1000 // Renovar 10 minutos antes de expirar
  private readonly MAX_REFRESH_ATTEMPTS_PER_CYCLE = 3 // 3 intentos por cada ciclo de renovación
  private readonly RETRY_DELAY = 5000 // 5 segundos entre reintentos

  // Datos del usuario autenticado
  private currentUserSubject = new BehaviorSubject<any>(this.getCurrentUser())
  public currentUser$ = this.currentUserSubject.asObservable()

  // Timers para manejo de expiración
  private refreshTimer?: Subscription
  private inactivityTimer?: Subscription
  private currentRefreshAttempts = 0 // Se resetea en cada ciclo exitoso

  // Credenciales para auto-refresh
  private lastCredentials: { email: string; password: string } | null = null

  // Estadísticas de sesión
  private totalRefreshes = 0
  private sessionStartTime = Date.now()

  // Inyectar dependencias usando constructor para evitar problemas de inicialización
  constructor(
    private apiService: ApiService,
    private router: Router
  ) {
    // Verificar si hay un token almacenado al iniciar el servicio
    this.checkInitialSession()

    // Configurar listeners para actividad del usuario
    this.setupActivityListeners()
  }

  private checkInitialSession(): void {
    if (this.hasToken()) {
      const isValid = this.isTokenValid()
      if (isValid) {
        this.isAuthenticatedSubject.next(true)
        this.setupSessionTimers()
      } else {
        this.logout("token_expired")
      }
    }
  }

  login(email: string, password: string): Observable<boolean> {

    return this.apiService.login(email, password).pipe(
      tap((response: LoginResponse) => {


        // Guardar credenciales para auto-refresh (solo en memoria)
        this.lastCredentials = { email, password }
        this.currentRefreshAttempts = 0
        this.totalRefreshes = 0
        this.sessionStartTime = Date.now()

        // Almacenar token y datos del usuario en localStorage
        localStorage.setItem("auth_token", response.token)
        localStorage.setItem("login_time", Date.now().toString())
        localStorage.setItem("last_activity", Date.now().toString())
        localStorage.setItem(
          "user_data",
          JSON.stringify({
            user: response.user,
            account: response.account,
            client: response.client,
            clientId: response.clientId,
          }),
        )

        // Actualizar estados
        this.isAuthenticatedSubject.next(true)
        this.currentUserSubject.next(response.user)

        // Configurar timers de expiración
        this.setupSessionTimers()
      }),
      map(() => true),
      catchError((error) => {
        console.error("Error en login:", error)
        this.logout("login_error")
        return throwError(() => error)
      }),
    )
  }

  private refreshToken(): Observable<boolean> {
    if (!this.lastCredentials) {
      return throwError(() => new Error("No hay credenciales para renovar token"))
    }

    this.currentRefreshAttempts++

    return this.apiService.refreshToken(this.lastCredentials.email, this.lastCredentials.password).pipe(
      tap((response: LoginResponse) => {
        this.totalRefreshes++

        // Actualizar token y mantener otros datos
        localStorage.setItem("auth_token", response.token)
        localStorage.setItem("last_activity", Date.now().toString())

        // Actualizar datos del usuario si han cambiado
        localStorage.setItem(
          "user_data",
          JSON.stringify({
            user: response.user,
            account: response.account,
            client: response.client,
            clientId: response.clientId,
          }),
        )

        // IMPORTANTE: Resetear contador de intentos tras éxito
        this.currentRefreshAttempts = 0

        // Reconfigurar timers con el nuevo token
        this.setupSessionTimers()
      }),
      map(() => true),
      retryWhen((errors) =>
        errors.pipe(
          tap((error) => {
          }),
          delay(this.RETRY_DELAY),
          take(this.MAX_REFRESH_ATTEMPTS_PER_CYCLE - 1), // -1 porque el primer intento no es retry
        ),
      ),
      catchError((error) => {
        console.error(`Error renovando token después de ${this.currentRefreshAttempts} intentos:`, error)

        // Solo cerrar sesión si agotamos todos los intentos de este ciclo
        if (this.currentRefreshAttempts >= this.MAX_REFRESH_ATTEMPTS_PER_CYCLE) {
          // Determinar la razón del error basándose en el mensaje
          const errorMessage = error.message?.toLowerCase() || '';
          if (errorMessage.includes('401') || errorMessage.includes('no autorizado')) {
            this.logout("credentials_invalid")
          } else {
            this.logout("connection_failed")
          }
        }

        return throwError(() => error)
      }),
    )
  }

  logout(reason = "manual"): void {
    const sessionDuration = Date.now() - this.sessionStartTime
    const sessionHours = Math.round((sessionDuration / 1000 / 60 / 60) * 10) / 10

    // Mostrar mensaje al usuario según la razón
    this.showLogoutMessage(reason)

    // Limpiar timers
    this.clearAllTimers()

    // Limpiar credenciales de memoria
    this.lastCredentials = null
    this.currentRefreshAttempts = 0
    this.totalRefreshes = 0

    // Eliminar todos los datos almacenados
    localStorage.removeItem("auth_token")
    localStorage.removeItem("user_data")
    localStorage.removeItem("login_time")
    localStorage.removeItem("last_activity")
    
    // Limpiar datos de pulseras del usuario al cerrar sesión
    this.clearUserBraceletsData()

    // Actualizar estados
    this.isAuthenticatedSubject.next(false)
    this.currentUserSubject.next(null)

    // Redirigir al login si no es logout manual
    if (reason !== "manual") {
      this.router.navigate(["/login"])
    }
  }

  // Limpiar datos de pulseras del localStorage al cerrar sesión
  private clearUserBraceletsData(): void {
    try {
      // Obtener todas las claves del localStorage
      const keys = Object.keys(localStorage)
      
      // Filtrar y eliminar solo las claves que empiecen con "bracelets_"
      const braceletKeys = keys.filter(key => key.startsWith("bracelets_"))
      
      braceletKeys.forEach(key => {
        localStorage.removeItem(key)
      })
      
      if (braceletKeys.length > 0) {
      }
    } catch (error) {
      console.error("Error al limpiar datos de pulseras:", error)
    }
  }

  private showLogoutMessage(reason: string): void {
    let message = ""
    let title = "Sesión cerrada"

    switch (reason) {
      case "token_expired":
        title = "Sesión expirada"
        message = "Tu sesión ha expirado y no se pudo renovar automáticamente."
        break
      case "inactivity_timeout":
        title = "Sesión expirada por inactividad"
        message = "Tu sesión se cerró automáticamente después de 7 días sin usar la aplicación."
        break
      case "credentials_invalid":
        title = "Credenciales inválidas"
        message = "Tus credenciales ya no son válidas. Es posible que tu contraseña haya cambiado."
        break
      case "connection_failed":
        title = "Error de conexión"
        message = "No se pudo mantener la conexión con el servidor después de varios intentos."
        break
      case "login_error":
        return // No mostrar mensaje para errores de login
      default:
        message = "Has cerrado sesión correctamente."
    }

    if (message) {
      alert(`${title}\n\n${message}`)
    }
  }

  private setupSessionTimers(): void {
    // Limpia cualquier timer previo para evitar duplicados/múltiples suscripciones.
    this.clearAllTimers()

    // Si no hay token, no hay nada que temporizar.
    const token = this.getToken()
    if (!token) return

    // Timer para renovación automática del token
    const tokenExpiration = this.getTokenExpiration()
    if (tokenExpiration) {
      // Tiempo que queda para que caduque el token (ms).
      const timeUntilExpiration = tokenExpiration - Date.now()
      // Queremos renovar un poco antes de la caducidad real.
      const timeUntilRefresh = timeUntilExpiration - this.REFRESH_BEFORE_EXPIRY

      if (timeUntilRefresh > 0) {


        //dispara una vez cuando transcurra 'timeUntilRefresh'
        this.refreshTimer = timer(timeUntilRefresh).subscribe(() => {

          // Intentamos renovar; el manejo de errores principal vive en refreshToken()
          this.refreshToken().subscribe({
            next: () => {
            },
            error: (error) => {
              console.error("Ciclo de renovación falló:", error)
              // El manejo de errores ya está en refreshToken()
            },
          })
        })
      } else if (timeUntilExpiration > 0) {
        // Si ya estamos dentro de la ventana de seguridad, renovamos de inmediato
        this.refreshToken().subscribe({
          // Si falla renovar en este punto, cerramos sesión por seguridad.
          error: () => {
            this.logout("token_expired")
          },
        })
      } else {
        // Token ya caducado: cerramos sesión.
        this.logout("token_expired")
        return
      }
    }

    // Timer basado en inactividad TOTAL (7 días sin abrir la app)
    const loginTime = localStorage.getItem("login_time")
    if (loginTime) {
      // Tiempo transcurrido desde el login inicial (ms)
      const timeSinceLogin = Date.now() - Number.parseInt(loginTime)
      // Tiempo restante hasta alcanzar el límite de inactividad.
      const timeUntilInactivityLogout = this.INACTIVITY_TIMEOUT - timeSinceLogin

      if (timeUntilInactivityLogout > 0) {
        const daysLeft = Math.round((timeUntilInactivityLogout / 1000 / 60 / 60 / 24) * 10) / 10

        // Programamos el cierre de sesión cuando se cumpla el umbral de inactividad.
        this.inactivityTimer = timer(timeUntilInactivityLogout).subscribe(() => {
          this.logout("inactivity_timeout")
        })
      } else {
         // Ya se superó el umbral de inactividad: cerramos sesión ahora.
        this.logout("inactivity_timeout")
        return
      }
    }
  }

  private setupActivityListeners(): void {
    // Escuchar actividad del usuario para actualizar last_activity
    const events = ["mousedown", "mousemove", "keypress", "scroll", "touchstart", "click"]

    // Agregar listeners para cada evento
    events.forEach((event) => {
      document.addEventListener(
        event,
        () => {
          // Solo actualizar si el usuario está logueado
          if (this.isLoggedIn()) {
            // Actualizar la última actividad, guardandola en local storage
            this.updateLastActivity()
          }
        },
        { passive: true },
      )
    })
  }

  private updateLastActivity(): void {
    localStorage.setItem("last_activity", Date.now().toString())
    // NOTA: NO reseteamos el timer de inactividad total aquí
    // Solo actualizamos la marca de tiempo de última actividad
  }

  private clearAllTimers(): void {
    if (this.refreshTimer) {
      this.refreshTimer.unsubscribe()
      this.refreshTimer = undefined
    }
    if (this.inactivityTimer) {
      this.inactivityTimer.unsubscribe()
      this.inactivityTimer = undefined
    }
  }

  // Verificar si hay un token almacenado
  private isTokenValid(): boolean {
    const token = this.getToken()
    if (!token) return false

    const expiration = this.getTokenExpiration()
    if (!expiration) return false

    return expiration > Date.now()
  }

  // Obtener la fecha de expiración del token (en ms desde epoch)
  private getTokenExpiration(): number | null {
    const token = this.getToken()
    if (!token) return null

    try {
      const payload = this.decodeJWT(token)
      return payload.exp * 1000 // Convertir a milisegundos
    } catch (error) {
      console.error("Error decodificando token:", error)
      return null
    }
  }

  // Decodificar el payload de un JWT
  private decodeJWT(token: string): TokenPayload {
    const parts = token.split(".")
    if (parts.length !== 3) {
      throw new Error("Token JWT inválido")
    }

    const payload = parts[1]
    const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"))
    return JSON.parse(decoded)
  }

  


  isLoggedIn(): boolean {
    return this.hasToken() && this.isTokenValid()
  }

  // Obtener el token de autenticación desde localStorage
  getToken(): string | null {
    return localStorage.getItem("auth_token")
  }

  // Obtener solo los datos del usuario desde localStorage
  getCurrentUser(): any {
    const userData = localStorage.getItem("user_data")
    if (userData) {
      try {
        const parsedData = JSON.parse(userData)
        return parsedData.user
      } catch (error) {
        console.error("Error parsing user data:", error)
        return null
      }
    }
    return null
  }

  // Obtener datos completos del usuario (user + account + client) desde localStorage
  getUserData(): any {
    const userData = localStorage.getItem("user_data")
    if (userData) {
      try {
        return JSON.parse(userData)
      } catch (error) {
        console.error("Error parsing user data:", error)
        return null
      }
    }
    return null
  }

  getEventUser(accountId: string, userId: string, query = ""): Observable<any> {
    const token = this.getToken()
    if (!token) {
      return throwError(() => new Error("No hay token de autenticación"))
    }

    return this.apiService.getEventUser(accountId, userId, token, query).pipe(
      tap((response) => {
        this.updateLastActivity()
      }),
      catchError((error) => {
        console.error("Error obteniendo datos de evento:", error)

        // Si el token expiró, intentar renovarlo automáticamente
        const errorMessage = error.message?.toLowerCase() || '';
        if (errorMessage.includes('401') || errorMessage.includes('no autorizado')) {
          return this.refreshToken().pipe(
            tap(() => {
            }),
            catchError(() => {
              this.logout("token_expired")
              return throwError(() => new Error("Token expirado y no se pudo renovar"))
            }),
          )
        }

        return throwError(() => error)
      }),
    )
  }

  private hasToken(): boolean {
    return !!localStorage.getItem("auth_token")
  }

  // Método público para forzar una renovación del token después de una importación
  public forceTokenRenewal(): Observable<boolean> {
    
    // Resetear contadores de intentos para asegurar un ciclo limpio
    this.currentRefreshAttempts = 0
    
    return this.refreshToken().pipe(
      tap(() => {
        // Reconfigurar timers con el nuevo token
        this.setupSessionTimers()
        // Actualizar última actividad
        this.updateLastActivity()
      }),
      catchError((error) => {
        console.error("Error renovando token después de importación:", error)
        
        // Si el token actual sigue siendo válido, permitir continuar
        if (this.isTokenValid()) {
          return of(true)
        }
        
        return throwError(() => error)
      })
    )
  }












  // FUNCIONES QUE NO SE USAN 
  // Método para extender la sesión manualmente
  //NO SE USA
  extendSession(): void {
    if (this.isLoggedIn()) {
      this.updateLastActivity()

      // Si el token está próximo a expirar, renovarlo
      const tokenExpiration = this.getTokenExpiration()
      if (tokenExpiration) {
        const timeUntilExpiration = tokenExpiration - Date.now()
        if (timeUntilExpiration < this.REFRESH_BEFORE_EXPIRY) {
          this.refreshToken().subscribe({
            next: () => {
              alert("Sesión extendida correctamente")
            },
            error: () => {
              alert("Error al extender la sesión")
            },
          })
        } else {
          // Solo reconfigurar timers
          this.setupSessionTimers()
          alert("Sesión extendida correctamente")
        }
      }
    }
  }

  // Método público para obtener información de la sesión
  //NO SE USA
  getSessionInfo(): {
    loginTime: Date | null
    lastActivity: Date | null
    tokenExpiration: Date | null
    timeUntilExpiration: number | null
    timeUntilInactivityLogout: number | null
    timeUntilRefresh: number | null
    totalRefreshes: number
    sessionDuration: number
  } {
    const loginTime = localStorage.getItem("login_time")
    const lastActivity = localStorage.getItem("last_activity")
    const tokenExpiration = this.getTokenExpiration()

    const timeUntilExpiration = tokenExpiration ? tokenExpiration - Date.now() : null
    const timeUntilInactivityLogout = loginTime
      ? this.INACTIVITY_TIMEOUT - (Date.now() - Number.parseInt(loginTime))
      : null
    const timeUntilRefresh =
      timeUntilExpiration && timeUntilExpiration > this.REFRESH_BEFORE_EXPIRY
        ? timeUntilExpiration - this.REFRESH_BEFORE_EXPIRY
        : null

    return {
      loginTime: loginTime ? new Date(Number.parseInt(loginTime)) : null,
      lastActivity: lastActivity ? new Date(Number.parseInt(lastActivity)) : null,
      tokenExpiration: tokenExpiration ? new Date(tokenExpiration) : null,
      timeUntilExpiration,
      timeUntilInactivityLogout,
      timeUntilRefresh,
      totalRefreshes: this.totalRefreshes,
      sessionDuration: Date.now() - this.sessionStartTime,
    }
  }
}
